import { createClient, type RealtimeChannel } from '@supabase/supabase-js';

import { createSessionGuard } from '@joshua/supabase-guard';
import type { Database } from '@joshua/types/database.generated';
import { setRefreshHealthSource } from '@/lib/refreshHealth';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

/**
 * JOSH-89: the session guard wraps every request of the main client. A data,
 * file or function request rejected for an expired token gets one lock-safe
 * refresh; during a refresh cooldown it is answered locally with
 * `SESSION_RECONNECTING`. Its health feeds `@/lib/refreshHealth`.
 */
const sessionGuard = createSessionGuard({ supabaseUrl });

const supabaseConfig = {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
  global: {
    headers: {
      'X-Client-Info': 'joshua-app/1.0',
    },
    fetch: sessionGuard.fetch,
  },
  db: {
    schema: 'public',
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
} as const;

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, supabaseConfig);
sessionGuard.bind(supabase);
setRefreshHealthSource(sessionGuard.health);

type SubscribeStatus = Parameters<Parameters<RealtimeChannel['subscribe']>[0] & {}>[0];
type SubscribeCallback = (status: SubscribeStatus, err?: Error) => void;

/**
 * Safe subscribe to a Supabase Realtime channel.
 *
 * On iPad Safari, creating a WebSocket can throw "The operation is insecure."
 * synchronously inside useEffect, tearing down the whole React tree. This
 * wrapper keeps the app running (without realtime) instead of crashing.
 */
export function safeSubscribe(
  channel: RealtimeChannel,
  onStatus?: SubscribeCallback,
): RealtimeChannel {
  try {
    channel.subscribe((status, err) => {
      if (err) {
        console.warn('[Supabase Realtime] Channel error:', err.message || err);
      }
      if (onStatus) {
        onStatus(status as SubscribeStatus, err);
      }
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.warn('[Supabase Realtime] Failed to subscribe (WebSocket unavailable):', message);
  }
  return channel;
}

// ---------------------------------------------------------------------------
// Global safety-net: suppresses ONLY the two known iPad Safari WebSocket
// failure modes that escape the safeSubscribe try-catch. Everything else must
// propagate so real bugs surface in Sentry (installed in P5).
//
// Keep the patterns tight — expand only with a new documented incident.
// ---------------------------------------------------------------------------
const IPAD_SAFARI_WS_PATTERNS: RegExp[] = [
  /WebSocket.*closed.*before.*established/i,
  /The operation is insecure/i,
];

const isKnownIPadSafariWsNoise = (message: string | undefined): boolean => {
  if (!message) return false;
  return IPAD_SAFARI_WS_PATTERNS.some((rx) => rx.test(message));
};

// ---------------------------------------------------------------------------
// P3-004 — silence `AuthSessionMissingError` on cold visits.
// GoTrueClient logs this to console.error internally when bootstrap finds
// no session (expected behavior for unauthenticated visitors).
// We wrap console.error once to drop that single pattern; everything else
// still reaches Sentry / devtools.
// ---------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  const _origConsoleError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    const firstArg = args[0];
    if (firstArg instanceof Error && firstArg.name === 'AuthSessionMissingError') {
      return;
    }
    if (typeof firstArg === 'string' && /AuthSessionMissingError/.test(firstArg)) {
      return;
    }
    _origConsoleError(...args);
  };

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason as { message?: string; name?: string } | string | undefined;
    const msg =
      typeof reason === 'object' && reason !== null ? reason.message : String(reason ?? '');
    const name = typeof reason === 'object' && reason !== null ? reason.name : undefined;
    if (name === 'AuthSessionMissingError') {
      event.preventDefault();
      return;
    }
    if (isKnownIPadSafariWsNoise(msg)) {
      console.warn('[Supabase Realtime] Suppressed known iPad Safari WS rejection:', msg);
      event.preventDefault();
    }
  });

  const _origOnError = window.onerror;
  window.addEventListener('error', (event) => {
    const msg = event.message || '';
    if (isKnownIPadSafariWsNoise(msg)) {
      console.warn('[Supabase Realtime] Suppressed known iPad Safari WS error:', msg);
      event.preventDefault();
      return true;
    }
    if (_origOnError) {
      return _origOnError.call(
        window,
        event.message,
        event.filename,
        event.lineno,
        event.colno,
        event.error,
      );
    }
    return undefined;
  });
}

/**
 * Fetch the default system bank account.
 */
export const getContaPadrao = async () => {
  try {
    const { data, error } = await supabase
      .from('conta')
      .select('id, nome, banco:banco_id(nome)')
      .eq('padrao', true)
      .single();

    if (error) {
      console.warn('[Supabase] Nenhuma conta padrão encontrada:', error.message);
      return null;
    }

    return data;
  } catch (err) {
    console.error('[Supabase] Erro ao buscar conta padrão:', err);
    return null;
  }
};
